import { API_BASE } from './apiEndpoints';
import { parseJsonResponse } from './httpJson.ts';
import {
  DEFAULT_CODE_CUSTOM_THEME_TOKENS,
  DEFAULT_CUSTOM_THEME_TOKENS,
  isEditorBackgroundMode,
  isSciplexCodeThemeId,
  isSciplexSqlThemeId,
  type CodeCustomThemeTokens,
  type EditorBackgroundMode,
  type SciplexCodeThemeId,
  type SciplexSqlThemeId,
  type SqlCustomThemeTokens,
} from './monacoThemes.ts';

export type EditorSettings = {
  sqlSyntaxTheme: SciplexSqlThemeId | null;
  sqlCustomThemeTokens: SqlCustomThemeTokens;
  sqlEditorBackgroundMode: EditorBackgroundMode;
  codeSyntaxTheme: SciplexCodeThemeId | null;
  codeCustomThemeTokens: CodeCustomThemeTokens;
  codeEditorBackgroundMode: EditorBackgroundMode;
};

type EditorSettingsResponse = {
  sql_syntax_theme: string | null;
  sql_custom_theme_tokens: Partial<SqlCustomThemeTokens> | null;
  sql_editor_background_mode: string | null;
  code_syntax_theme: string | null;
  code_custom_theme_tokens: Partial<CodeCustomThemeTokens> | null;
  code_editor_background_mode: string | null;
};

function mergeSqlCustomTokens(raw: Partial<SqlCustomThemeTokens> | null | undefined): SqlCustomThemeTokens {
  const merged = { ...DEFAULT_CUSTOM_THEME_TOKENS };
  if (!raw || typeof raw !== 'object') return merged;

  for (const key of Object.keys(merged) as Array<keyof SqlCustomThemeTokens>) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim()) {
      merged[key] = value.trim().toUpperCase().startsWith('#')
        ? value.trim().toUpperCase()
        : `#${value.trim().toUpperCase()}`;
    }
  }

  return merged;
}

function mergeCodeCustomTokens(raw: Partial<CodeCustomThemeTokens> | null | undefined): CodeCustomThemeTokens {
  const merged = { ...DEFAULT_CODE_CUSTOM_THEME_TOKENS };
  if (!raw || typeof raw !== 'object') return merged;

  for (const key of Object.keys(merged) as Array<keyof CodeCustomThemeTokens>) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim()) {
      merged[key] = value.trim().toUpperCase().startsWith('#')
        ? value.trim().toUpperCase()
        : `#${value.trim().toUpperCase()}`;
    }
  }

  return merged;
}

function parseBackgroundMode(value: string | null | undefined): EditorBackgroundMode {
  if (value && isEditorBackgroundMode(value)) return value;
  return 'sciplex';
}

function mapResponse(data: EditorSettingsResponse): EditorSettings {
  const sqlTheme =
    data.sql_syntax_theme && isSciplexSqlThemeId(data.sql_syntax_theme)
      ? data.sql_syntax_theme
      : null;
  const codeTheme =
    data.code_syntax_theme && isSciplexCodeThemeId(data.code_syntax_theme)
      ? data.code_syntax_theme === 'sciplex-code'
        ? 'sciplex-code-classic'
        : data.code_syntax_theme
      : null;

  return {
    sqlSyntaxTheme: sqlTheme,
    sqlCustomThemeTokens: mergeSqlCustomTokens(data.sql_custom_theme_tokens),
    sqlEditorBackgroundMode: parseBackgroundMode(data.sql_editor_background_mode),
    codeSyntaxTheme: codeTheme,
    codeCustomThemeTokens: mergeCodeCustomTokens(data.code_custom_theme_tokens),
    codeEditorBackgroundMode: parseBackgroundMode(data.code_editor_background_mode),
  };
}

export async function fetchEditorSettings(): Promise<EditorSettings | null> {
  try {
    const response = await fetch(`${API_BASE}/api/editor-settings`, {
      credentials: 'include',
    });
    if (response.status === 401) return null;
    const data = await parseJsonResponse<EditorSettingsResponse>(response);
    if (!response.ok) return null;
    return mapResponse(data);
  } catch {
    return null;
  }
}

/** @deprecated используйте fetchEditorSettings */
export const fetchSqlEditorSettings = fetchEditorSettings;

export async function saveEditorSettings(settings: {
  sqlSyntaxTheme?: SciplexSqlThemeId;
  sqlCustomThemeTokens?: SqlCustomThemeTokens;
  sqlEditorBackgroundMode?: EditorBackgroundMode;
  codeSyntaxTheme?: SciplexCodeThemeId;
  codeCustomThemeTokens?: CodeCustomThemeTokens;
  codeEditorBackgroundMode?: EditorBackgroundMode;
}): Promise<boolean> {
  try {
    const body: Record<string, unknown> = {};
    if (settings.sqlSyntaxTheme != null) {
      body.sql_syntax_theme = settings.sqlSyntaxTheme;
    }
    if (settings.sqlCustomThemeTokens != null) {
      body.sql_custom_theme_tokens = settings.sqlCustomThemeTokens;
    }
    if (settings.sqlEditorBackgroundMode != null) {
      body.sql_editor_background_mode = settings.sqlEditorBackgroundMode;
    }
    if (settings.codeSyntaxTheme != null) {
      body.code_syntax_theme = settings.codeSyntaxTheme;
    }
    if (settings.codeCustomThemeTokens != null) {
      body.code_custom_theme_tokens = settings.codeCustomThemeTokens;
    }
    if (settings.codeEditorBackgroundMode != null) {
      body.code_editor_background_mode = settings.codeEditorBackgroundMode;
    }

    const response = await fetch(`${API_BASE}/api/editor-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** @deprecated используйте saveEditorSettings */
export const saveSqlEditorSettings = saveEditorSettings;
